import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../Layout';
import aoAPI from '../../api/aoAPI';
import ClientSearchSelect from './ClientSearchSelect';
import {
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const TAX_TYPES = [
  { value: 'PT',            label: 'PT — Percentage Tax' },
  { value: 'VAT',           label: 'VAT — Value-Added Tax' },
  { value: 'WE',            label: 'WE — Withholding Expanded' },
  { value: 'Excise',        label: 'Excise — Excise Tax' },
  { value: 'DST',           label: 'DST — Documentary Stamp Tax' },
  { value: 'ITR',           label: 'ITR — Income Tax Return' },
  { value: 'PF',            label: 'PF — Professional Fee' },
  { value: 'SEC',           label: 'SEC Processing' },
  { value: 'BIR',           label: 'BIR Processing' },
  { value: 'BusinessPermit',label: 'Business Permit' },
  { value: 'TaxClearance',  label: 'Tax Clearance' },
  { value: 'PhilGeps',      label: 'PhilGeps' },
  { value: 'Notarial',      label: 'Notarial' },
  { value: 'DocStamp',      label: 'Doc Stamp' },
  { value: 'SSS',      label: 'SSS' },
  { value: 'PhilHealth',      label: 'PhilHealth' },
  { value: 'PAG-IBIG',      label: 'PAG-IBIG' },
  { value: 'OtherExpenses', label: 'Other Expenses' },
];

const MONTHS = [
  { value: 1, label: 'January' },   { value: 2, label: 'February' },
  { value: 3, label: 'March' },     { value: 4, label: 'April' },
  { value: 5, label: 'May' },       { value: 6, label: 'June' },
  { value: 7, label: 'July' },      { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const QUARTERS = [
  { value: 1, label: 'Q1', months: [1, 2, 3] },
  { value: 2, label: 'Q2', months: [4, 5, 6] },
  { value: 3, label: 'Q3', months: [7, 8, 9] },
  { value: 4, label: 'Q4', months: [10, 11, 12] },
];

const BILLING_TABS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const quarterOfMonth = (m) => QUARTERS.find(q => q.months.includes(m))?.value ?? 1;

const EMPTY_FORM = {
  fiscalYear:      new Date().getFullYear(),
  month:           new Date().getMonth() + 1,
  quarter:         quarterOfMonth(new Date().getMonth() + 1),
  taxType:         'PT',
  amount:          '',
  isPaid:          false,
  paidDate:        '',
  remarks:         '',
  transactionTime: '',
};

const fmt = (n) =>
  n ? new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2 }).format(n) : '—';

const CycleBadge = ({ cycle }) => {
  if (!cycle) return null;
  return cycle === 'monthly' ? (
    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
      Monthly
    </span>
  ) : (
    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
      Quarterly
    </span>
  );
};

const AOTransactionScreen = () => {
  const [clients, setClients]     = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [fyFilter, setFyFilter]   = useState(new Date().getFullYear());
  const [transactions, setTxns]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [form, setForm]           = useState(EMPTY_FORM);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [editId, setEditId]       = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState('');

  // load clients once
  useEffect(() => {
    aoAPI.getClients().then(r => setClients(r.data || [])).catch(() => {});
  }, []);

  const loadTxns = useCallback(async () => {
    if (!selectedClient) return;
    try {
      setLoading(true);
      const res = await aoAPI.getTransactions(selectedClient, { fiscalYear: fyFilter });
      setTxns(res.data || []);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [selectedClient, fyFilter]);

  useEffect(() => { loadTxns(); }, [loadTxns]);

  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
    if (cycle === 'quarterly') {
      const q = QUARTERS.find(x => x.value === quarterOfMonth(form.month));
      setForm(f => ({ ...f, quarter: q.value, month: q.months[0] }));
      return;
    }
    setForm(f => ({ ...f, quarter: quarterOfMonth(f.month) }));
  };

  const handleQuarterChange = (quarter) => {
    const selectedQuarter = QUARTERS.find(q => q.value === quarter);
    if (!selectedQuarter) return;
    setForm(f => ({ ...f, quarter, month: selectedQuarter.months[0] }));
  };

  const handleSave = async () => {
    if (!selectedClient)     { setSaveMsg('Select a client first'); return; }
    if (!form.amount || isNaN(Number(form.amount))) { setSaveMsg('Enter a valid amount'); return; }
    try {
      setSaving(true);
      setSaveMsg('');
      const payload = {
        fiscalYear:      parseInt(form.fiscalYear, 10),
        month:           parseInt(form.month, 10),
        quarter:         quarterOfMonth(parseInt(form.month, 10)),
        taxType:         form.taxType,
        billingCycle,
        amount:          parseFloat(form.amount),
        isPaid:          form.isPaid,
        paidDate:        form.paidDate || null,
        remarks:         form.remarks,
        transactionTime: form.transactionTime || new Date().toISOString(),
      };
      if (editId) {
        await aoAPI.updateTransaction(editId, payload);
      } else {
        await aoAPI.upsertTransaction(selectedClient, payload);
      }
      setSaveMsg('Saved!');
      setEditId(null);
      setForm(EMPTY_FORM);
      setBillingCycle('monthly');
      await loadTxns();
    } catch (e) {
      setSaveMsg(typeof e === 'string' ? e : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t) => {
    setEditId(t._id);
    setForm({
      fiscalYear:      t.fiscalYear,
      month:           t.month,
      quarter:         quarterOfMonth(t.month),
      taxType:         t.taxType,
      amount:          t.amount,
      isPaid:          t.isPaid,
      paidDate:        t.paidDate ? t.paidDate.substring(0, 10) : '',
      remarks:         t.remarks || '',
      transactionTime: t.transactionTime ? t.transactionTime.substring(0, 16) : '',
    });
    setBillingCycle(t.billingCycle || 'monthly');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await aoAPI.deleteTransaction(id);
      await loadTxns();
    } catch (e) {
      alert(typeof e === 'string' ? e : 'Delete failed');
    }
  };

  // Group transactions by taxType for display
  const grouped = transactions.reduce((acc, t) => {
    if (!acc[t.taxType]) acc[t.taxType] = [];
    acc[t.taxType].push(t);
    return acc;
  }, {});

  const monthLabel = (m) => MONTHS.find(x => x.value === m)?.label || m;

  return (
    <Layout pageTitle="Transaction Entry">
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-5">
          <h2 className="text-xl font-bold text-gray-800">Transaction Entry</h2>
          <p className="text-xs text-gray-500 mt-0.5">Record tax payments, PF, Benefits and processing fees per client per month.</p>
        </div>

        {/* Client + FY selector row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Client *</label>
            <ClientSearchSelect
              clients={clients}
              value={selectedClient}
              onChange={(id) => { setSelectedClient(id); setTxns([]); }}
              placeholder="— Search client —"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fiscal Year</label>
            <input
              type="number"
              value={fyFilter}
              onChange={e => setFyFilter(parseInt(e.target.value, 10))}
              min={2000} max={2100}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Entry form */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            {editId ? '✏️ Editing Transaction' : '➕ New Transaction'}
          </h3>

          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5 mb-4">
            {BILLING_TABS.map(tab => (
              <button
                key={tab.value}
                onClick={() => handleBillingCycleChange(tab.value)}
                className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  billingCycle === tab.value
                    ? 'bg-white text-teal-700 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Year</label>
              <input
                type="number"
                value={form.fiscalYear}
                onChange={e => setForm(f => ({ ...f, fiscalYear: e.target.value }))}
                min={2000} max={2100}
                className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            {billingCycle === 'monthly' ? (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Month</label>
                <select
                  value={form.month}
                  onChange={e => setForm(f => ({ ...f, month: parseInt(e.target.value, 10), quarter: quarterOfMonth(parseInt(e.target.value, 10)) }))}
                  className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Quarter</label>
                  <select
                    value={form.quarter}
                    onChange={e => handleQuarterChange(parseInt(e.target.value, 10))}
                    className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {QUARTERS.map(q => (
                      <option key={q.value} value={q.value}>{q.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Payment Month</label>
                  <select
                    value={form.month}
                    onChange={e => setForm(f => ({ ...f, month: parseInt(e.target.value, 10) }))}
                    className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {(QUARTERS.find(q => q.value === form.quarter)?.months || []).map(monthValue => {
                      const label = MONTHS.find(m => m.value === monthValue)?.label || monthValue;
                      return <option key={monthValue} value={monthValue}>{label}</option>;
                    })}
                  </select>
                </div>
              </>
            )}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Transaction Type</label>
              <select
                value={form.taxType}
                onChange={e => setForm(f => ({ ...f, taxType: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {TAX_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
              <input
                type="number"
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
                min={0}
                step={0.01}
                className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isPaid}
                  onChange={e => setForm(f => ({ ...f, isPaid: e.target.checked }))}
                  className="h-4 w-4 rounded accent-teal-600"
                />
                <span className="text-sm text-gray-600">Paid</span>
              </label>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Paid Date</label>
              <input
                type="date"
                value={form.paidDate}
                onChange={e => setForm(f => ({ ...f, paidDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Transaction Time</label>
              <input
                type="datetime-local"
                value={form.transactionTime}
                onChange={e => setForm(f => ({ ...f, transactionTime: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-1">Remarks</label>
            <input
              type="text"
              value={form.remarks}
              onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
              placeholder="Optional notes…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {saveMsg && (
            <p className={`text-xs mb-3 ${saveMsg === 'Saved!' ? 'text-green-600' : 'text-red-500'}`}>
              {saveMsg}
            </p>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-5 py-2 rounded-lg disabled:opacity-60 transition-colors"
            >
              {saving ? 'Saving…' : editId ? 'Update' : 'Save Transaction'}
            </button>
            {editId && (
              <button
                onClick={() => { setEditId(null); setForm(EMPTY_FORM); setBillingCycle('monthly'); }}
                className="border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm px-4 py-2 rounded-lg"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </div>

        {/* Transaction list */}
        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

        {selectedClient && (
          loading ? (
            <p className="text-sm text-gray-400">Loading transactions…</p>
          ) : transactions.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No transactions recorded for this client / year.</p>
          ) : (
            <div className="space-y-5">
              {Object.entries(grouped).map(([taxType, rows]) => (
                <div key={taxType} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
                    {taxType}
                    <span className="text-gray-400 font-normal normal-case">
                      ({rows.length} record{rows.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-white border-b border-gray-100 text-xs text-gray-500">
                      <tr>
                        <th className="px-4 py-2 text-left">Period</th>
                        <th className="px-4 py-2 text-center">Cycle</th>
                        <th className="px-4 py-2 text-right">Amount</th>
                        <th className="px-4 py-2 text-center">Status</th>
                        <th className="px-4 py-2 text-left">Paid Date</th>
                        <th className="px-4 py-2 text-left">Remarks</th>
                        <th className="px-4 py-2 text-center w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {rows.sort((a, b) => a.month - b.month).map(t => (
                        <tr key={t._id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-700">
                            {t.billingCycle === 'quarterly'
                              ? `Q${quarterOfMonth(t.month)} · ${monthLabel(t.month)}`
                              : monthLabel(t.month)}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <CycleBadge cycle={t.billingCycle || 'monthly'} />
                          </td>
                          <td className="px-4 py-2 text-right font-mono font-medium text-gray-800">
                            ₱{fmt(t.amount)}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {t.isPaid ? (
                              <span className="flex items-center justify-center gap-1 text-green-600 text-xs font-medium">
                                <CheckCircleIcon className="h-4 w-4" /> Paid
                              </span>
                            ) : (
                              <span className="text-amber-500 text-xs font-medium">Pending</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-gray-500 text-xs">
                            {t.paidDate ? new Date(t.paidDate).toLocaleDateString('en-PH') : '—'}
                          </td>
                          <td className="px-4 py-2 text-gray-400 text-xs truncate max-w-[160px]">{t.remarks || '—'}</td>
                          <td className="px-4 py-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => startEdit(t)} className="text-gray-400 hover:text-teal-600" title="Edit">
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleDelete(t._id)} className="text-gray-400 hover:text-red-500" title="Delete">
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </Layout>
  );
};

export default AOTransactionScreen;
